import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { api } from '../lib/api.jsx';
import { useDashboard } from './dashboardContext.jsx';
import { useTableSort } from '../lib/useTableSort.jsx';

const inputS =
  'w-full bg-card border border-rule-2 rounded-lg px-3 py-2 text-fg-2 text-[13px] outline-none box-border';

export function RolesPage() {
  const { reloadPerms } = useDashboard();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const blankPerms = {
    name: '',
    description: '',
    canViewClients: false,
    canManageClients: false,
    canManageUsers: false,
    canManageProvisioning: false,
    canManageConfig: false,
    canViewAudit: false,
    canViewDevices: false,
    canDeleteDevices: false,
    canViewInventory: false,
    canManageAlerts: false,
    canRunCommands: false,
    canRemoteConnect: false,
    canInstallUpdates: false,
    canManageVulnerabilities: false,
  };
  const [form, setForm] = useState(blankPerms);
  const { sortRows, headerProps, arrow } = useTableSort('name', 'asc');
  const load = async () => {
    const r = await api.get('/roles');
    setRoles(r || []);
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);
  const editing = editingId ? roles.find((r) => r.id === editingId) : null;
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      ...r,
    });
  };
  const closeEdit = () => {
    setEditingId(null);
    setForm(blankPerms);
  };
  const handleCreate = async () => {
    if (!form.name) return alert('Nom requis');
    await api.post('/roles', form);
    setShowCreate(false);
    setForm(blankPerms);
    load();
  };
  const handleUpdate = async () => {
    await api.put('/roles/' + editingId, form);
    closeEdit();
    load();
    reloadPerms();
  };
  const handleDelete = async (r) => {
    if (r.isSystem) return alert('Les rôles système ne peuvent être supprimés');
    if (r.usersCount > 0) return alert('Rôle utilisé par ' + r.usersCount + ' utilisateur(s)');
    if (!confirm('Supprimer le rôle "' + r.name + '" ?')) return;
    await api.del('/roles/' + r.id);
    load();
  };
  const permGroups = [
    {
      title: 'Administration',
      perms: [
        ['canViewClients', 'Voir clients'],
        ['canManageClients', 'Gérer clients et sites'],
        ['canManageUsers', 'Gérer utilisateurs, rôles, groupes'],
        ['canManageProvisioning', 'Gérer codes de provisionnement'],
        ['canManageConfig', 'Modifier la configuration système'],
        ['canViewAudit', "Consulter les journaux d'audit"],
      ],
    },
    {
      title: 'Appareils',
      perms: [
        ['canViewDevices', 'Voir les appareils'],
        ['canDeleteDevices', 'Supprimer des appareils'],
        ['canViewInventory', "Voir l'inventaire détaillé"],
      ],
    },
    {
      title: 'Actions sur appareils',
      perms: [
        ['canManageAlerts', 'Gérer les alertes'],
        ['canRunCommands', 'Exécuter commandes / scripts'],
        ['canRemoteConnect', 'Connexion à distance (RDP)'],
        ['canInstallUpdates', 'Installer Windows Updates'],
        ['canManageVulnerabilities', 'Gérer les vulnérabilités CVE'],
      ],
    },
  ];
  const inputS =
    'w-full bg-card border border-rule-2 rounded-lg px-3 py-2 text-fg-2 text-[13px] outline-none box-border';
  const permForm = (values, setValues) => (
    <div>
      <div
        className="grid gap-3 mb-3.5"
        style={{
          gridTemplateColumns: '1fr 1fr',
        }}
      >
        <input
          placeholder="Nom du rôle"
          value={values.name || ''}
          onChange={(e) =>
            setValues({
              ...values,
              name: e.target.value,
            })
          }
          className={inputS}
        />
        <input
          placeholder="Description"
          value={values.description || ''}
          onChange={(e) =>
            setValues({
              ...values,
              description: e.target.value,
            })
          }
          className={inputS}
        />
      </div>
      {permGroups.map((g) => (
        <div key={g.title} className="mb-3.5">
          <div
            className="text-[11px] font-bold text-muted-2 uppercase mb-2"
            style={{
              letterSpacing: '0.05em',
            }}
          >
            {g.title}
          </div>
          <div
            className="grid gap-1.5"
            style={{
              gridTemplateColumns: '1fr 1fr',
            }}
          >
            {g.perms.map(([key, label]) => (
              <label
                key={key}
                className={`flex items-center gap-2 py-2 px-3 rounded-lg cursor-pointer text-xs border ${
                  values[key]
                    ? 'bg-blue-500/[.08] border-blue-500/30 text-fg-2'
                    : 'bg-sidebar border-rule-2 text-muted'
                }`}
              >
                <input
                  type="checkbox"
                  checked={values[key] || false}
                  onChange={(e) =>
                    setValues({
                      ...values,
                      [key]: e.target.checked,
                    })
                  }
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h1
          className="text-[22px] font-bold text-fg"
          style={{
            margin: 0,
          }}
        >
          {'Rôles (' + roles.length + ')'}
        </h1>
        <button
          onClick={() => {
            setShowCreate(!showCreate);
            setForm(blankPerms);
          }}
          className="bg-blue-500 border-none rounded-lg py-2 px-[18px] text-white cursor-pointer text-xs font-semibold"
        >
          + Nouveau rôle
        </button>
      </div>
      {showCreate && (
        <div className="bg-card border border-rule rounded-2xl p-5 mb-5">
          <div className="text-sm font-bold text-blue-400 mb-3.5">Nouveau rôle</div>
          {permForm(form, setForm)}
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleCreate}
              className="bg-ok border-none rounded-lg py-2 px-5 text-white cursor-pointer text-xs font-semibold"
            >
              Créer
            </button>
            <button
              onClick={() => setShowCreate(false)}
              className="border border-rule-3 rounded-lg py-2 px-4 text-muted cursor-pointer text-xs"
              style={{
                background: 'none',
              }}
            >
              Annuler
            </button>
          </div>
        </div>
      )}
      {loading ? (
        <div className="text-muted-2">Chargement...</div>
      ) : (
        <div className="bg-card border border-rule rounded-2xl overflow-hidden">
          <table
            className="w-full text-[12.5px]"
            style={{
              borderCollapse: 'collapse',
            }}
          >
            <thead>
              <tr className="border-b border-rule">
                {[
                  { key: 'name', label: 'Nom' },
                  { key: 'description', label: 'Description' },
                  { key: 'usersCount', label: 'Utilisateurs' },
                  { key: 'permCount', label: 'Permissions' },
                  { key: null, label: 'Actions' },
                ].map((c) => (
                  <th
                    key={c.label}
                    {...headerProps(c.key)}
                    className="py-2.5 px-3.5 text-left text-muted-2 font-medium text-[11px] uppercase whitespace-nowrap"
                  >
                    {c.label}
                    {arrow(c.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortRows(roles, (r, key) => {
                if (key === 'permCount')
                  return permGroups.reduce((n, g) => n + g.perms.filter(([k]) => r[k]).length, 0);
                return r[key];
              }).map((r) => {
                const permCount = permGroups.reduce((n, g) => n + g.perms.filter(([k]) => r[k]).length, 0);
                return (
                  <tr key={r.id} className="border-b border-rule cursor-pointer" onClick={() => openEdit(r)}>
                    <td className="py-3 px-3.5">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-semibold text-fg">{r.name}</span>
                        {r.isSystem && (
                          <span
                            className="text-[9px] py-0.5 px-2 rounded text-violet-400 font-bold uppercase"
                            style={{
                              background: 'rgba(139,92,246,0.15)',
                              letterSpacing: '0.05em',
                            }}
                          >
                            Système
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-3.5 text-muted-2 text-xs">{r.description || '—'}</td>
                    <td
                      className="py-3 px-3.5 text-xs"
                      style={{
                        color: r.usersCount > 0 ? '#e2e8f0' : '#475569',
                      }}
                    >
                      {r.usersCount}
                    </td>
                    <td className="py-3 px-3.5">
                      <span
                        className="rounded-md text-[11px] font-semibold text-blue-400"
                        style={{
                          padding: '3px 10px',
                          background: 'rgba(59,130,246,0.12)',
                        }}
                      >
                        {permCount + ' permission(s)'}
                      </span>
                    </td>
                    <td className="py-3 px-3.5" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => openEdit(r)}
                          className="border border-rule-3 rounded-md py-1 px-2.5 text-blue-400 cursor-pointer text-[11px]"
                          style={{
                            background: 'none',
                          }}
                        >
                          Modifier
                        </button>
                        {!r.isSystem && (
                          <button
                            onClick={() => handleDelete(r)}
                            className="rounded-md py-1 px-2.5 text-red-300 cursor-pointer text-[11px]"
                            style={{
                              background: 'none',
                              border: '1px solid #7f1d1d',
                            }}
                          >
                            Supprimer
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1000]"
          onClick={closeEdit}
        >
          <div
            className="bg-card border border-rule rounded-2xl flex flex-col overflow-hidden w-[640px] max-w-[92vw] max-h-[85vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="py-5 px-6 flex justify-between items-center border-b border-rule">
              <div>
                <div className="text-lg font-bold text-fg">Modifier le rôle</div>
                {editing.isSystem && (
                  <div className="text-[11px] text-violet-400 mt-1">
                    Rôle système — modifiable mais non supprimable
                  </div>
                )}
              </div>
              <button
                onClick={closeEdit}
                className="border-none bg-transparent text-muted-2 cursor-pointer p-1 hover:text-fg-2"
              >
                <X size={18} strokeWidth={1.75} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">{permForm(form, setForm)}</div>
            <div className="py-4 px-6 border-t border-rule flex justify-end gap-2">
              <button
                onClick={closeEdit}
                className="border border-rule-3 bg-transparent rounded-lg py-2 px-4 text-muted cursor-pointer text-xs hover:bg-card-hi"
              >
                Annuler
              </button>
              <button
                onClick={handleUpdate}
                className="bg-blue-500 border-none rounded-lg py-2 px-5 text-white cursor-pointer text-xs font-semibold hover:bg-blue-600"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
